import React from 'react';

import i18n from 'util/i18n';

import { Summary, DateSummary } from 'components/Summary';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';

import { PSYCHOLOGICAL, PSYCHOLOGICAL_DIAGNOSES } from 'config/formSections/psychological';

import Branch from 'components/Form/Branch';
import Accordion from 'components/Form/Accordion';
import Field from 'components/Form/Field';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import ErrorMessage from 'components/ErrorMessage';
import { diagnosesAddressNameFields, psychologicalTreatmentAddressNameFields } from 'constants/enums/people';
import { isPVQForm } from 'helpers/form';
import { PVQ_A_B_D } from 'constants/formTypes';
import Diagnosis from './Diagnosis';
import Treatment from '../Treatment';

const sectionConfig = {
  key: PSYCHOLOGICAL_DIAGNOSES.key,
  section: PSYCHOLOGICAL.name,
  store: PSYCHOLOGICAL.store,
  subsection: PSYCHOLOGICAL_DIAGNOSES.name,
  storeKey: PSYCHOLOGICAL_DIAGNOSES.storeKey,
};

export class Diagnoses extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateDiagnosed = this.updateDiagnosed.bind(this);
    this.updateDiagnosisList = this.updateDiagnosisList.bind(this);
    this.updateDidNotConsult = this.updateDidNotConsult.bind(this);
    this.updateInTreatment = this.updateInTreatment.bind(this);
    this.updateTreatmentList = this.updateTreatmentList.bind(this);
    this.updateComments = this.updateComments.bind(this);
    this.updateSummaryComments = this.updateSummaryComments.bind(this);
    this.updateDidNotConsultComments = this.updateDidNotConsultComments.bind(this);
    this.updateInTreatmentComments = this.updateInTreatmentComments.bind(this);
    this.updateInTreatmentSummaryComments = this.updateInTreatmentSummaryComments.bind(this);
    this.summary = this.summary.bind(this);
    this.treatmentSummary = this.treatmentSummary.bind(this);
  }

  update = (queue) => {
    this.props.onUpdate(this.storeKey, {
      Diagnosed: this.props.Diagnosed,
      DidNotConsult: this.props.DidNotConsult,
      InTreatment: this.props.InTreatment,
      DiagnosisList: this.props.DiagnosisList,
      TreatmentList: this.props.TreatmentList,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      DidNotConsultComments: this.props.DidNotConsultComments,
      InTreatmentComments: this.props.InTreatmentComments,
      InTreatmentSummaryComments: this.props.InTreatmentSummaryComments,
      ...queue,
    });
  };

  updateDiagnosisList = (values) => {
    this.update({
      DiagnosisList: values,
    });
  };

  updateTreatmentList = (values) => {
    this.update({
      TreatmentList: values,
    });
  };

  updateDiagnosed = (values) => {
    this.update({
      Diagnosed: values,
      DiagnosisList: values.value === 'Yes' ? this.props.DiagnosisList : [],
      DidNotConsult: values.value === 'Yes' ? this.props.DidNotConsult : '',
      InTreatment: values.value === 'Yes' ? this.props.InTreatment : '',
      TreatmentList: values.value === 'Yes' ? this.props.TreatmentList : [],
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
      DidNotConsultComments: values.value === 'Yes' ? this.props.DidNotConsultComments : {},
      InTreatmentComments: values.value === 'Yes' ? this.props.InTreatmentComments : {},
      InTreatmentSummaryComments: values.value === 'Yes' ? this.props.InTreatmentSummaryComments : {},
    });
  };

  updateDidNotConsult = (values) => {
    this.update({
      DidNotConsult: values,
    });
  };

  updateInTreatment = (values) => {
    this.update({
      InTreatment: values,
      TreatmentList: values.value === 'Yes' ? this.props.TreatmentList : [],
    });
  };

  updateComments(values) {
    this.update({
      Comments: values,
    });
  }

  updateSummaryComments(values) {
    this.update({
      SummaryComments: values,
    });
  }

  updateDidNotConsultComments(values) {
    this.update({
      DidNotConsultComments: values,
    });
  }

  updateInTreatmentComments(values) {
    this.update({
      InTreatmentComments: values,
    });
  }

  updateInTreatmentSummaryComments(values) {
    this.update({
      InTreatmentSummaryComments: values,
    });
  }

  showInTreatment = () => (
    <Branch
      name="inTreatment"
      attribute="InTreatment"
      label={i18n.t('psychological.diagnoses.heading.inTreatment')}
      labelSize="h4"
      className="intreatment"
      {...this.props.InTreatment}
      warning
      onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
      required={this.props.required}
      onUpdate={this.updateInTreatment} // eslint-disable-line @typescript-eslint/unbound-method
      scrollIntoView={this.props.scrollIntoView}
    />
  );

  summary = (item, index) => {
    const diagnosisItem = (item || {}).Item || {};
    const date = (diagnosisItem || {}).Diagnosed || {};
    // const diagnosisDate = dateRangeFormat(date)
    const diagnosisDate = DateSummary(date);
    const facility = (diagnosisItem.Condition || {}).value || '';

    return Summary({
      type: i18n.t('psychological.diagnoses.collection.itemType'),
      index,
      left: facility,
      right: diagnosisDate,
      placeholder: i18n.t('psychological.diagnoses.collection.summary'),
    });
  };

  treatmentSummary = (item, index) => {
    const o = (item || {}).Treatment || {};
    const name = (o.Name || {}).value || '';

    return Summary({
      type: i18n.t('psychological.diagnoses.treatment.collection.itemType'),
      index,
      left: name,
      placeholder: i18n.t('psychological.diagnoses.treatment.collection.summary'),
    });
  };

  render() {
    const {
      errors, peopleWhoKnowYou, DiagnosisList, TreatmentList
    } = this.props;

    const diagnosisErrors = errors && errors.filter((e) => e.indexOf('DiagnosisList.accordion') === 0);
    const treatmentErrors = errors && errors.filter((e) => e.indexOf('TreatmentList.accordion') === 0);

    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(DiagnosisList?.items, peopleWhoKnowYou, diagnosesAddressNameFields);
    const duplicateTreatmentPeopleWhoKnowYou = getDuplicateNamesAndAddresses(TreatmentList?.items, peopleWhoKnowYou, psychologicalTreatmentAddressNameFields);

    const duplicatePeopleWhoKnowYouError = (errorsList) => (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (errorsList) => (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...errorsList.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    const sectionPrefix = (this.props.formType === PVQ_A_B_D) ? 'Part D:' : 'Section 28:';
    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div
        className="section-content diagnoses"
        data-section={PSYCHOLOGICAL.key}
        data-subsection={PSYCHOLOGICAL_DIAGNOSES.key}
      >
        <h1 className="section-header">{`${sectionPrefix} ${i18n.t('psychological.pvq.destination.diagnoses')}`}</h1>
        <Branch
          name="diagnosed"
          attribute="Diagnosed"
          label={i18n.t('psychological.pvq.diagnoses.heading.diagnoses')}
          labelSize="h4"
          className="diagnosed"
          {...this.props.Diagnosed}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          onUpdate={this.updateDiagnosed} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          showDNK
        />
        {this.props.Diagnosed?.value === 'Yes' && (
          <div>
            <Accordion
              attribute="DiagnosisList"
              className="diagnosis-collection"
              defaultState={this.props.defaultState}
              {...this.props.DiagnosisList}
              onUpdate={this.updateDiagnosisList} // eslint-disable-line @typescript-eslint/unbound-method
              summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              errors={diagnosisErrors}
              description={i18n.t('psychological.pvq.diagnoses.collection.description')}
              appendTitle={i18n.t('psychological.pvq.diagnoses.collection.appendTitle')}
              appendLabel={i18n.t('psychological.pvq.diagnoses.collection.appendLabel')}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              additionalErrors={
                (
                  duplicatePeopleWhoKnowYou.errors.length > 0
                  && duplicatePeopleWhoKnowYouError(duplicatePeopleWhoKnowYou)
                )
                || (
                  duplicatePeopleWhoKnowYou.warnings.length > 0
                  && duplicatePeopleWhoKnowYouWarning(duplicatePeopleWhoKnowYou)
                )
              }
            >
              <Diagnosis
                attribute="DiagnosisList"
                name="Item"
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                addressBooks={this.props.addressBooks}
                dispatch={this.props.dispatch}
                bind
                formType={this.props.formType}
              />
            </Accordion>

          </div>
        )}
      </div>
    ) : (
      <div
        className="section-content diagnoses"
        data-section={PSYCHOLOGICAL.key}
        data-subsection={PSYCHOLOGICAL_DIAGNOSES.key}
      >
        <h1 className="section-header">{i18n.t('psychological.destination.diagnoses')}</h1>
        {i18n.t('psychological.heading.diagnoses')}
        <Branch
          name="diagnosed"
          attribute="Diagnosed"
          label={i18n.t('psychological.diagnoses.heading.diagnoses')}
          labelSize="h4"
          className="diagnosed"
          {...this.props.Diagnosed}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          onUpdate={this.updateDiagnosed} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.t('psychological.diagnoses.heading.examples')}
        </Branch>
        <Comments
          title={i18n.t('psychological.diagnoses.comments')}
          name="Diagnoses AnswerComment"
          className="optional-comments"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        {this.props.Diagnosed.value === 'Yes' && (
        <div>
          <Accordion
            attribute="DiagnosisList"
            className="diagnosis-collection"
            defaultState={this.props.defaultState}
            {...this.props.DiagnosisList}
            onUpdate={this.updateDiagnosisList} // eslint-disable-line @typescript-eslint/unbound-method
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={diagnosisErrors}
            description={i18n.t('psychological.diagnoses.collection.description')}
            appendTitle={i18n.t('psychological.diagnoses.collection.appendTitle')}
            appendMessage={i18n.m('psychological.diagnoses.collection.appendMessage')}
            appendLabel={i18n.t('psychological.diagnoses.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
                  (
                    duplicatePeopleWhoKnowYou.errors.length > 0
                    && duplicatePeopleWhoKnowYouError(duplicatePeopleWhoKnowYou)
                  )
                  || (
                    duplicatePeopleWhoKnowYou.warnings.length > 0
                    && duplicatePeopleWhoKnowYouWarning(duplicatePeopleWhoKnowYou)
                  )
                }
          >
            <Diagnosis
              attribute="DiagnosisList"
              name="Item"
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              bind
            />
          </Accordion>
          <Comments
            title={i18n.t('psychological.diagnoses.summaryComments')}
            name="SummaryComments"
            className="optional-comments"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
          <Branch
            name="didNotConsult"
            attribute="DidNotConsult"
            label={i18n.t('psychological.diagnoses.heading.didNotConsult')}
            labelSize="h4"
            className="didnotconsult"
            {...this.props.DidNotConsult}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            onUpdate={this.updateDidNotConsult} // eslint-disable-line @typescript-eslint/unbound-method
            scrollIntoView={this.props.scrollIntoView}
          />
          <Comments
            title={i18n.t('psychological.diagnoses.didNotConsultComments')}
            name="DidNotConsultComments"
            className="optional-comments"
            attribute="DidNotConsultComments"
            {...this.props.DidNotConsultComments}
            onUpdate={this.updateDidNotConsultComments}
            required={false}
          />
          {this.props.DidNotConsult?.value === 'Yes' && (
          <><Branch
            name="inTreatment"
            attribute="InTreatment"
            label={i18n.t('psychological.diagnoses.heading.inTreatment')}
            labelSize="h4"
            className="intreatment"
            {...this.props.InTreatment}
            warning
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            onUpdate={this.updateInTreatment} // eslint-disable-line @typescript-eslint/unbound-method
            scrollIntoView={this.props.scrollIntoView}
          />
            <Comments
              title={i18n.t('psychological.diagnoses.inTreatmentComments')}
              name="InTreatmentComments"
              className="optional-comments"
              attribute="InTreatmentComments"
              {...this.props.InTreatmentComments}
              onUpdate={this.updateInTreatmentComments}
              required={false}
            />
          </>
          )}
          {this.props.InTreatment?.value === 'Yes' && (
          <>
            <Accordion
              defaultState={this.props.defaultState}
              {...this.props.TreatmentList}
              scrollToBottom={this.props.scrollToBottom}
              onUpdate={this.updateTreatmentList} // eslint-disable-line @typescript-eslint/unbound-method
              summary={this.treatmentSummary} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
              errors={treatmentErrors}
              appendTitle={i18n.t('psychological.diagnoses.treatment.collection.appendTitle')}
              appendLabel={i18n.t('psychological.diagnoses.treatment.collection.appendLabel')}
              required={this.props.required}
              attribute="TreatmentList"
              scrollIntoView={this.props.scrollIntoView}
              additionalErrors={
                      (
                        duplicateTreatmentPeopleWhoKnowYou.errors.length > 0
                        && duplicatePeopleWhoKnowYouError(duplicateTreatmentPeopleWhoKnowYou)
                      )
                      || (
                        duplicateTreatmentPeopleWhoKnowYou.warnings.length > 0
                        && duplicatePeopleWhoKnowYouWarning(duplicateTreatmentPeopleWhoKnowYou)
                      )
                    }
            >
              <Field
                title={i18n.t('psychological.diagnosis.heading.healthcareProfessional')}
                titleSize="h4"
                optional
                className="no-margin-bottom"
              />
              <Treatment
                attribute="TreatmentList"
                name="Item"
                prefix="diagnoses.professional"
                addressBooks={this.props.addressBooks}
                dispatch={this.props.dispatch}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                includeComment
                bind
              />
            </Accordion>
            <Comments
              title={i18n.t('psychological.diagnoses.inTreatmentSummaryComments')}
              name="InTreatmentSummaryComments"
              className="optional-comments"
              attribute="InTreatmentSummaryComments"
              {...this.props.InTreatmentSummaryComments}
              onUpdate={this.updateInTreatmentSummaryComments}
              required={false}
            />
          </>
          )}
        </div>
        )}
      </div>
    );
  }
}

Diagnoses.defaultProps = {
  List: {},
  Diagnosed: {},
  DidNotConsult: {},
  InTreatment: {},
  DiagnosisList: {},
  TreatmentList: {},
  Comments: {},
  DidNotConsultComments: {},
  SummaryComments: {},
  InTreatmentComments: {},
  InTreatmentSummaryComments: {},
  defaultState: true,
  onUpdate: () => { },
  onError: (value, arr) => arr,
  addressBooks: {},
  dispatch: () => { },
  scrollToBottom: '.bottom-btns',
  errors: [],
  formType: '',
};

export default connectSubsection(Diagnoses, sectionConfig);
